/**
 * Shared fuzzy scoring utility.
 *
 * Returns a score > 0 when `query` matches `target`, 0 when it doesn't.
 *
 * Priority tiers:
 *   100, full query is an exact substring of target
 *    20, per word: exact substring match
 *     8, per word: subsequence match (chars appear in order)
 *     4, per word: one-character deletion typo tolerance (words >= 4 chars)
 *     0, any word matched nothing -> whole item rejected
 */
export function scoreMatch(target: string, query: string): number {
  const targetLower = target.toLowerCase();
  const queryLower = (query || '').toLowerCase().trim();
  if (!queryLower) return 0;

  // Full-query exact substring, highest priority
  if (targetLower.includes(queryLower)) return 100;

  const words = queryLower.split(/\s+/).filter(Boolean);
  let total = 0;

  for (const w of words) {
    // Exact word substring
    if (targetLower.includes(w)) { total += 20; continue; }

    // Subsequence (chars in order), handles abbreviations like "chn ltng"
    let ti = 0, wi = 0;
    while (ti < targetLower.length && wi < w.length) {
      if (targetLower[ti] === w[wi]) wi++;
      ti++;
    }
    if (wi === w.length) { total += 8; continue; }

    // One-char typo tolerance (deletion) for words >= 4 chars
    if (w.length >= 4) {
      let typoMatch = false;
      for (let i = 0; i < w.length && !typoMatch; i++) {
        if (targetLower.includes(w.slice(0, i) + w.slice(i + 1))) typoMatch = true;
      }
      if (typoMatch) { total += 4; continue; }
    }

    // This word matched nothing, reject the whole item
    return 0;
  }

  return total;
}

/**
 * Filter + sort a list of items by fuzzy query.
 * @param items     - array of objects
 * @param query     - user input
 * @param getText   - (item) => string to score against
 * @param getTitle  - (item) => string for title boost (2x)
 * @returns sorted array of matching items
 */
export function fuzzyFilter<T>(
  items: T[],
  query: string,
  getText: (item: T) => string,
  getTitle?: (item: T) => string,
): T[] {
  if (!query || !query.trim()) return items;
  return items
    .map((item) => {
      const textScore = scoreMatch(getText(item) || '', query);
      const titleScore = getTitle ? scoreMatch(getTitle(item) || '', query) * 2 : 0;
      return { item, _score: Math.max(textScore, titleScore) };
    })
    .filter(({ _score }) => _score > 0)
    .sort((a, b) => b._score - a._score)
    .map(({ item }) => item);
}
